import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import Loading from '../Common/Loading';
import { UseOnboardingStatus } from '../Hooks/UseOnboarding';

const RoleGuardWithRoles = ({ children, requiredRoles }) => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [hasValidAccess, setHasValidAccess] = useState(false);
  
  // Refs to prevent multiple checks and unnecessary re-renders
  const hasCheckedRef = useRef(false);
  const currentUserIdRef = useRef(null);
  
  // Use the onboarding hook
  const { needsOnboarding, isChecking: onboardingChecking } = UseOnboardingStatus(user, isAuthenticated);

  // SINGLE SOURCE OF TRUTH: Backend subscription check
  const checkSubscriptionAccess = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://inkedupmanager.com/api'}/subscription/details/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Subscription check failed: ${response.status}`);
      }

      const data = await response.json();
      
      const hasSubscription = data.has_subscription === true;
      const subscriptionTier = data.tier;
      const isActive = data.status === 'active';
      
      console.log('Subscription check:', {
        hasSubscription,
        subscriptionTier,
        isActive,
        requiredRoles
      });

      if (!hasSubscription || !isActive || !subscriptionTier || subscriptionTier === 'unpaid') {
        return false;
      }

      // Map subscription tiers to accessible roles
      const tierAccessMap = {
        'basic': ['basic_subscriber'],
        'standard': ['basic_subscriber', 'standard_subscriber'],
        'starter_studio': [
          'basic_subscriber', 
          'standard_subscriber', 
          'starter_studio_subscriber', 
          'starter_studio_member'
        ],
        'growth_studio': [
          'basic_subscriber', 
          'standard_subscriber', 
          'starter_studio_subscriber', 
          'starter_studio_member',
          'growth_studio_subscriber', 
          'growth_studio_member'
        ]
      };

      const userAccessRoles = tierAccessMap[subscriptionTier] || [];
      const hasRequiredAccess = requiredRoles.some(role => userAccessRoles.includes(role));
      
      console.log('Access decision:', {
        subscriptionTier,
        userAccessRoles,
        requiredRoles,
        hasRequiredAccess
      });

      return hasRequiredAccess;
      
    } catch (error) {
      console.error('Subscription check error:', error);
      return false;
    }
  };

  // Main effect - use user?.sub instead of user object to prevent reference changes
  useEffect(() => {
    const performCheck = async () => {
      if (!isAuthenticated || isLoading) return;
      
      // Only check once per user session
      const userId = user?.sub;
      if (hasCheckedRef.current && currentUserIdRef.current === userId) {
        return;
      }

      try {
        setLoading(true);
        const hasAccess = await checkSubscriptionAccess();
        setHasValidAccess(hasAccess);
        
        // Mark as checked for this user
        hasCheckedRef.current = true;
        currentUserIdRef.current = userId;
        
        if (hasAccess) {
        } else {
        }
        
      } catch (error) {
        console.error('Access check failed:', error);
        setHasValidAccess(false);
      } finally {
        setLoading(false);
      }
    };

    performCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, user?.sub]);

  // Show loading only on INITIAL load, not on re-checks
  if (isLoading || (loading && !hasCheckedRef.current) || onboardingChecking) {
    return <Loading message="Verifying subscription access..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Check access - only redirect if we've finished checking
  if (!hasValidAccess && !loading) {
    return <Navigate to="/subscription" replace />;
  }

  // Check onboarding
  if (needsOnboarding === true) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

export default RoleGuardWithRoles;